import { Component } from 'react';
import ReactSelect, { createFilter, components } from 'react-select';
import ReactSelectCreatable from 'react-select/creatable';
import styled from 'styled-components';
import { theme } from '../theme';
import { FormItem, ValidationState } from '../Form/FormItem';
import { Icon } from '../Icon/Icon';
import MenuList from './SelectMenuList';
import { sortOptions } from '../../utils/sorting';
import { SvgIcon } from '../SvgIcons/svgIcons';

const RenderSelect = props => {
  return props.children({ ...props });
};

const StyledSelect = styled(ReactSelect)`
  ${props => props.theme.partials.newForm.item.default};
`;

const StyledCreatableSelect = styled(ReactSelectCreatable)`
  ${props => props.theme.partials.newForm.item.default};
`;

export const IconWrapper = styled.div`
  display: inline-block;
  margin-right: 0.8rem;
  min-width: 16px;
`;

const SelecetedItem = styled.div`
  font: ${({ theme }) => theme.designToken.body2};
  overflow: hidden;
  color: ${({ theme }) => theme.designToken.neutral900};
  text-overflow: ellipsis;
`;

const DropDownItem = styled.div<{ catergoryLevel: number }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  span {
    font: ${({ theme }) => theme.designToken.body2};
  }
`;

export const ReactSelectStyles = {
  container: styles => ({
    ...styles,
    padding: 0,
  }),
  control: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? theme.vars.newColor.darkGS3 : null,
    background: isDisabled ? theme.vars.newColor.lightGS2 : theme.vars.newColor.white,
    border: 'none',
    boxShadow: 'none',
    display: 'flex',
    padding: `0 ${theme.designToken.spacing3}`,
    alignItems: 'center',
    minHeight: '48px',
    width: '100%',
    font: `${theme.designToken.body2}`,

    ':hover': {
      boxShadow: 'none',
    },
  }),
  valueContainer: styles => ({
    ...styles,
    padding: `0 ${theme.designToken.spacing2} 0 0`,
  }),
  singleValue: styles => ({
    ...styles,
    margin: 0,
    padding: 0,

    '> div': {
      display: 'none',
    },
  }),
  placeholder: styles => ({
    ...styles,
    color: theme.vars.newColor.lightGS1,
  }),
  input: styles => ({
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    padding: 0,
    font: theme.designToken.body2,
    color: theme.designToken.neutral700,
    transform: state.selectProps.menuIsOpen && 'rotate(180deg)',
  }),
  clearIndicator: styles => ({
    ...styles,
    padding: 0,
    marginRight: theme.designToken.spacing1,
  }),
  menu: styles => ({
    ...styles,
    zIndex: 2,
    borderRadius: '0',
    boxShadow: 'none',
    margin: `${theme.designToken.spacing1} 0`,
    border: `1px solid ${theme.vars.newColor.lightGS1}`,
  }),
  menuList: styles => ({
    ...styles,
    padding: '0',
    margin: '0',
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? theme.designToken.primary700 : isFocused ? theme.designToken.primary50 : null,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: '375',
    padding: `${theme.designToken.spacing2} ${theme.designToken.spacing3}`,

    svg: {
      color: isSelected ? 'white' : null,
    },
  }),
};

export interface ISelectIcon {
  name: string;
  color?: string;
}

export interface ISelectOption<T> {
  value: T;
  label: string;
  icon?: ISelectIcon;
}

/** @deprecated Do not use. Use `ISelectOption` instead */
export interface ILegacySelectItem<T> {
  id: T;
  label: string;
}

export type MenuPlacement = 'auto' | 'bottom' | 'below' | 'top' | 'above';

export interface IProps<T> {
  /** Select label */
  label?: string;
  /** ARIA label tag */
  ariaLabel?: string;
  /** Will be passed through to the labels for attribute */
  inputId?: string;
  /**
   * The placeholder to display when no value is specified. Defaults to "Choose
   * a value".
   */
  placeholder?: string;
  /** The selected item. Undefined by default. */
  value?: ISelectOption<T>;
  /** Item to be selected on first load. Undefined by default. */
  defaultValue?: ISelectOption<T>;
  /** The list of items to select from. {value:string, label:string}. */
  options?: Array<ISelectOption<T>>;
  /**
   * LEGACY: The list of items to select from. {id:string, label:string}.
   * @deprecated Do not use. Use `options` instead
   */
  items?: Array<ILegacySelectItem<T>>;
  /** Event invoked when a new value is selected. */
  onChange?: (value: ISelectOption<T>) => void;
  /** Event invoked when a new item is created. */
  onCreateOption?: (inputValue: string) => void;
  /** Input validation state (complete, warning).  */
  validationState?: ValidationState;
  /** Input validation message.  */
  validationMessage?: string;
  /** Adds spacing below the component (default: true). */
  spacingBelow?: boolean;
  /** A hint can be passed as HTML to explain what the select is used for. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string | string[];
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /**
   * The placement of the menu. One of "auto", "bottom", or "top" (defaults to
   * "bottom").
   */
  menuPlacement?: MenuPlacement;
  /** Height of the menu in pixels. Defaults to 300. */
  menuHeight?: number;
  /** Whether to disable the select or not.  Defaults to false. */
  disabled?: boolean;
  /** Whether you can search the options. Defaults to true. */
  searchable?: boolean;
  /** Minimum search term length before options are displayed. Defaults to 0. */
  minLength?: number;
  /** Placeholder which appears if more characters must be typed to search results */
  minLengthPlaceholder?: string;
  /** Placeholder which appears when there are no options to display */
  noOptionsPlaceholder?: string;
  /** Whether you can clear the selected value. Defaults to false. */
  clearable?: boolean;
  /** Whether or not the menu is open on load. Defaults to false. */
  defaultMenuOpen?: boolean;
  /** Whether or not to focus the menu on mount. Defaults to false. */
  autoFocus?: boolean;
  /**
   * react-select doesn't handle being inline very well. So we need to ensure
   * that when it's inline it sets FormItem to full width.
   */
  inline?: boolean;
  /** Whether or not new options can be created. */
  creatable?: boolean;
  /** Alphabetically sort the options by the label name.  */
  isSorted?: boolean;
}

export class Select<T = string> extends Component<IProps<T>> {
  public static defaultProps: Pick<
    IProps<string>,
    'minLength' | 'minLengthPlaceholder' | 'noOptionsPlaceholder' | 'searchable'
  > = {
    minLength: 0,
    minLengthPlaceholder: 'Type to search...',
    noOptionsPlaceholder: 'No options',
    searchable: true,
  };

  public transformOptions = (items: Array<ILegacySelectItem<T>>) => {
    /**
     * react-select requires the options to be in a seperate format to how we've
     * been providing the data to the old Select component. To prevent a breaking
     * change we can transform the legacy items into a format recognised by
     * react-select.
     */

    return items.map(item => ({
      label: item.label,
      value: item.id,
    }));
  };

  public transformSelectedOption = (selectedOption: ISelectOption<T>) => {
    return selectedOption ? selectedOption : null;
  };

  public render() {
    const {
      ariaLabel,
      autoFocus,
      clearable,
      creatable,
      defaultMenuOpen,
      defaultValue,
      disabled,
      hint,
      inline,
      inputId,
      isSorted,
      items,
      label,
      menuHeight,
      menuPlacement,
      minLength,
      minLengthPlaceholder,
      noOptionsPlaceholder,
      onChange,
      onCreateOption,
      options,
      placeholder,
      searchable,
      spacingBelow,
      tooltipDescription,
      tooltipHeading,
      tooltipWidth,
      validationMessage,
      validationState,
      value,
    } = this.props;

    const selectOptions = items ? this.transformOptions(items) : options;

    let selectMenuPlacement: MenuPlacement = menuPlacement;
    const components = selectOptions && selectOptions.length >= 100 && { MenuList };

    if (menuPlacement === 'above') {
      selectMenuPlacement = 'top';
    } else if (menuPlacement === 'below') {
      selectMenuPlacement = 'bottom';
    }

    return (
      <FormItem
        disabled={disabled}
        fullWidth={inline}
        hint={hint}
        label={label}
        spacingBelow={spacingBelow}
        tooltipDescription={tooltipDescription}
        tooltipHeading={tooltipHeading}
        tooltipWidth={tooltipWidth}
        validationMessage={validationMessage}
        validationState={validationState}
        for={inputId}
        type="select"
      >
        <RenderSelect
          aria-label={ariaLabel}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          className="select-container"
          classNamePrefix="select"
          components={{
            Option: this.CustomSelectOption,
            SingleValue: this.CustomSelectValue,
            DropdownIndicator: this.DropdownIndicator,
            ClearIndicator: this.DownChevron,
          }}
          MenuList={components}
          defaultMenuIsOpen={defaultMenuOpen}
          defaultValue={defaultValue}
          filterOption={this.createOptionFilter(minLength, searchable)}
          inputId={inputId}
          isClearable={clearable}
          isDisabled={disabled}
          isSearchable={searchable}
          maxMenuHeight={menuHeight}
          menuPlacement={selectMenuPlacement}
          noOptionsMessage={this.createNoOptionsMessage(
            minLength,
            searchable,
            minLengthPlaceholder,
            noOptionsPlaceholder,
          )}
          onChange={(selectedOption: ISelectOption<T>) => onChange(this.transformSelectedOption(selectedOption))}
          onCreateOption={onCreateOption}
          options={isSorted ? selectOptions.sort(sortOptions) : selectOptions}
          placeholder={placeholder || 'Choose a value'}
          styles={ReactSelectStyles}
          value={value}
        >
          {selectProps => (
            <>
              {!creatable && <StyledSelect {...selectProps} />}
              {creatable && <StyledCreatableSelect {...selectProps} />}
            </>
          )}
        </RenderSelect>
      </FormItem>
    );
  }

  private DropdownIndicator = props => {
    return (
      <components.DropdownIndicator {...props}>
        <SvgIcon name="angle-down" size="24" />
      </components.DropdownIndicator>
    );
  };

  private DownChevron = props => {
    return (
      <components.ClearIndicator {...props}>
        <SvgIcon name="xmark" size="24" color={theme.designToken.neutral500} />
      </components.ClearIndicator>
    );
  };

  private createOptionFilter = (minLength: number, isSearchable: boolean) => {
    const defaultFilter = createFilter({
      ignoreAccents: false,
      stringify: option => option.label,
    });

    if (!isSearchable || minLength === 0) {
      return defaultFilter;
    }

    return (candidate, input) => {
      if (!input || input.length < minLength) {
        return false;
      }

      return defaultFilter(candidate, input);
    };
  };

  private createNoOptionsMessage = (
    minLength: number,
    isSearchable: boolean,
    searchMessage: string,
    defaultMessage: string,
  ) => {
    if (!isSearchable || minLength === 0) {
      return () => defaultMessage;
    }

    return input => (!input.inputValue || input.inputValue.length < minLength ? searchMessage : defaultMessage);
  };

  private CustomSelectOption = props => {
    return (
      <components.Option {...props}>
        <DropDownItem catergoryLevel={props.data.categoryLevel}>
          {props.icon && (
            <IconWrapper>
              <Icon icon={props.icon.name} color={props.icon.color} />
            </IconWrapper>
          )}
          <span>{props.data.label}</span>
          {props.isSelected && <SvgIcon name="check" size="24" color={theme.designToken.baseWhite} />}
        </DropDownItem>
      </components.Option>
    );
  };

  private CustomSelectValue = props => {
    return <SelecetedItem>{props.data.label}</SelecetedItem>;
  };
}
